import React, { Component } from 'react'
import { withApollo } from 'react-apollo'
import gql from 'graphql-tag'
import { Card, CardHeader, CardBody, Row, Col, Table, Input, FormGroup, Label } from 'reactstrap'
import { Bar } from 'react-chartjs-2'
import { RiseLoader } from 'react-spinners'
import { store } from '../../stores/reduxStore'
import CreatableSelect from '../Wps/CreatableSelect'
import { ElementsFetchQuery } from './query'

const PROJECTS_QUERY = gql`
  query GroupingSavingsProjects {
    projects { list { id name project_companies } }
  }
`

const PERSON_QUERY = gql`
  query GroupingSavingsPerson($person_id: ID!) {
    person(id: $person_id) { company_ids }
  }
`

const DATA_QUERY = gql`
  query StatsGroupingSavings($project_id: ID!, $min_length: Int, $price_per_unit: Float) {
    stats_grouping_savings(project_id: $project_id, min_length: $min_length, price_per_unit: $price_per_unit) {
      inspection_type_id
      inspection_type_name
      total_welds_count
      actual_welds_count
      actual_without_units
      actual_with_units
      actual_savings_units
      actual_without_cost
      actual_with_cost
      actual_savings_cost
      actual_savings_pct
      potential_welds_count
      potential_without_units
      potential_with_units
      potential_savings_units
      potential_without_cost
      potential_with_cost
      potential_savings_cost
      potential_savings_pct
      min_length
      price_per_unit
    }
  }
`

class GroupingSavings extends Component {
  constructor(props) {
    super(props)
    const params = new URLSearchParams(window.location.search)
    this.state = {
      projects: [],
      project_id: params.get('project_id') || '',
      element: null,
      elements: [],
      min_length: 500,
      price_per_unit: 10,
      data: null,
      loading: false,
      error: null
    }
  }

  componentDidMount() {
    const { client } = this.props
    const personId = store.getState().user.data.person_id
    Promise.all([
      client.query({ query: PROJECTS_QUERY, fetchPolicy: 'network-only' }),
      client.query({ query: PERSON_QUERY, variables: { person_id: personId }, fetchPolicy: 'network-only' })
    ]).then(([pRes, meRes]) => {
      const userCompanyIds = (meRes.data.person.company_ids || []).map(String)
      const allProjects = pRes.data.projects.list
      const projects = allProjects.filter(p => {
        if (!userCompanyIds.length) return true
        const pc = typeof p.project_companies === 'string' ? JSON.parse(p.project_companies) : (p.project_companies || [])
        return pc.some(c => userCompanyIds.includes(String(c.id)))
      })
      this.setState({ projects }, () => {
        if (this.state.project_id) this.loadData()
      })
    })
  }

  loadData() {
    const { project_id, element, min_length, price_per_unit } = this.state
    if (!project_id) return
    const effective_id = element ? element.id : project_id
    this.setState({ loading: true, error: null })
    this.props.client.query({
      query: DATA_QUERY,
      variables: { project_id: effective_id, min_length: parseInt(min_length), price_per_unit: parseFloat(price_per_unit) },
      fetchPolicy: 'network-only'
    }).then(({ data }) => {
      this.setState({ data: data.stats_grouping_savings || [], loading: false })
    }).catch(e => {
      this.setState({ error: e.message, loading: false })
    })
  }

  handleProjectChange(project_id) {
    this.setState({ project_id, element: null, elements: [], data: null })
    const url = project_id ? `${window.location.pathname}?project_id=${project_id}` : window.location.pathname
    window.history.replaceState(null, '', url)
    if (project_id) {
      this.props.client.query({
        query: ElementsFetchQuery,
        fetchPolicy: 'network-only',
        variables: { parent_id: Number(project_id) }
      }).then(({ data }) => {
        const elements = data.elements.list.map(el => ({
          ...el,
          name: '\u00A0'.repeat((el.depth - 1) * 2) + el.name
        }))
        this.setState({ elements }, () => setTimeout(() => this.loadData(), 0))
      })
    }
  }

  handleElementChange(element) {
    this.setState({ element, data: null })
    const { project_id } = this.state
    const params = new URLSearchParams({ project_id, ...(element ? { element_id: element.id } : {}) })
    window.history.replaceState(null, '', `${window.location.pathname}?${params}`)
    setTimeout(() => this.loadData(), 0)
  }

  render() {
    const { projects, project_id, element, elements, min_length, price_per_unit, data, loading, error } = this.state

    const hasData = data && data.length > 0
    const actualTotal   = hasData ? data.reduce((s, r) => s + r.actual_savings_cost,    0) : 0
    const potentialTotal = hasData ? data.reduce((s, r) => s + r.potential_savings_cost, 0) : 0
    const actualWithout  = hasData ? data.reduce((s, r) => s + r.actual_without_cost,    0) : 0
    const potentialWithout = hasData ? data.reduce((s, r) => s + r.potential_without_cost, 0) : 0
    const actualPct    = actualWithout    > 0 ? (actualTotal    / actualWithout    * 100).toFixed(1) : 0
    const potentialPct = potentialWithout > 0 ? (potentialTotal / potentialWithout * 100).toFixed(1) : 0

    const labels = hasData ? data.map(r => r.inspection_type_name) : []

    const actualChart = hasData && {
      labels,
      datasets: [
        { label: 'Without grouping', data: data.map(r => r.actual_without_units.toFixed(1)), backgroundColor: 'rgba(248,108,107,0.7)' },
        { label: 'With grouping',    data: data.map(r => r.actual_with_units.toFixed(1)),    backgroundColor: 'rgba(32,168,216,0.7)' }
      ]
    }

    const potentialChart = hasData && {
      labels,
      datasets: [
        { label: 'Without grouping', data: data.map(r => r.potential_without_units.toFixed(1)), backgroundColor: 'rgba(248,108,107,0.7)' },
        { label: 'With grouping',    data: data.map(r => r.potential_with_units.toFixed(1)),    backgroundColor: 'rgba(77,189,116,0.7)' }
      ]
    }

    return (
      <div className="animated fadeIn">
        <Card>
          <CardHeader><strong><i className="fa fa-object-group mr-2" />Grouping Savings Analysis</strong></CardHeader>
          <CardBody style={{ position: 'relative', minHeight: '200px' }}>
            <Row className="mb-2">
              <Col sm={4}>
                <FormGroup>
                  <Label>Project</Label>
                  <Input type="select" value={project_id} onChange={e => this.handleProjectChange(e.target.value)}>
                    <option value="">— select project —</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </Input>
                </FormGroup>
              </Col>
              <Col sm={4}>
                <CreatableSelect
                  label="Element"
                  id="element-select"
                  options={elements}
                  valueKey="id"
                  value={element}
                  multi={false}
                  creatable={false}
                  valueChangeFunction={val => this.handleElementChange(val)}
                />
              </Col>
            </Row>
            <Row className="mb-3 align-items-end">
              <Col sm={2}>
                <FormGroup className="mb-0">
                  <Label>Min length (mm)</Label>
                  <Input type="number" value={min_length} min={1}
                    onChange={e => this.setState({ min_length: e.target.value })} />
                </FormGroup>
              </Col>
              <Col sm={2}>
                <FormGroup className="mb-0">
                  <Label>Price / unit</Label>
                  <Input type="number" value={price_per_unit} min={0} step={0.01}
                    onChange={e => this.setState({ price_per_unit: e.target.value })} />
                </FormGroup>
              </Col>
              <Col sm={2}>
                <button className="btn btn-primary" onClick={() => this.loadData()} disabled={!project_id || loading}>Generate</button>
              </Col>
            </Row>

            {loading && <div className="spinner-item" id="loader-bg"><RiseLoader color="#20a8d8" loading={true} /></div>}
            {error && <div className="text-danger">Error: {error}</div>}
            {!project_id && !loading && <div className="text-muted">Select a project to view data.</div>}

            {hasData && (
              <div>
                {/* Actual + Potential side by side */}
                <Row className="mb-4">
                  <Col md="6">
                    <div className="p-3 border rounded h-100">
                      <h6><strong>Actual savings</strong> — welds already in groups</h6>
                      <Row className="mb-2">
                        <Col xs="6">
                          <div className="text-center p-2 border rounded">
                            <div className="text-muted small">Savings</div>
                            <div className={`h5 mb-0 ${actualTotal >= 0 ? 'text-success' : 'text-danger'}`}><strong>{actualTotal.toFixed(2)} PLN</strong></div>
                          </div>
                        </Col>
                        <Col xs="6">
                          <div className="text-center p-2 border rounded">
                            <div className="text-muted small">Savings %</div>
                            <div className={`h5 mb-0 ${actualTotal >= 0 ? 'text-success' : 'text-danger'}`}><strong>{actualPct}%</strong></div>
                          </div>
                        </Col>
                      </Row>
                      <Bar data={actualChart} options={{ maintainAspectRatio: true, scales: { yAxes: [{ ticks: { beginAtZero: true }, scaleLabel: { display: true, labelString: `units (${min_length}mm)` } }] } }} />
                      <Table bordered striped size="sm" responsive className="mt-2 mb-0">
                        <thead><tr><th>Type</th><th>Total welds</th><th>In groups</th><th>W/o (units)</th><th>With (units)</th><th>Savings</th><th>%</th></tr></thead>
                        <tbody>
                          {data.map((r, i) => (
                            <tr key={i}>
                              <td><strong>{r.inspection_type_name}</strong></td>
                              <td>{r.total_welds_count}</td>
                              <td>{r.actual_welds_count}</td>
                              <td>{r.actual_without_units.toFixed(1)}</td>
                              <td>{r.actual_with_units.toFixed(1)}</td>
                              <td style={{ color: r.actual_savings_cost >= 0 ? 'green' : 'red' }}><strong>{r.actual_savings_cost.toFixed(2)}</strong></td>
                              <td style={{ color: r.actual_savings_pct >= 0 ? 'green' : 'red' }}>{r.actual_savings_pct}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="p-3 border rounded h-100">
                      <h6><strong>Potential savings</strong> — ungrouped welds (estimated)</h6>
                      <Row className="mb-2">
                        <Col xs="6">
                          <div className="text-center p-2 border rounded">
                            <div className="text-muted small">Savings</div>
                            <div className="h5 mb-0 text-info"><strong>{potentialTotal.toFixed(2)} PLN</strong></div>
                          </div>
                        </Col>
                        <Col xs="6">
                          <div className="text-center p-2 border rounded">
                            <div className="text-muted small">Savings %</div>
                            <div className="h5 mb-0 text-info"><strong>{potentialPct}%</strong></div>
                          </div>
                        </Col>
                      </Row>
                      <Bar data={potentialChart} options={{ maintainAspectRatio: true, scales: { yAxes: [{ ticks: { beginAtZero: true }, scaleLabel: { display: true, labelString: `units (${min_length}mm)` } }] } }} />
                      <Table bordered striped size="sm" responsive className="mt-2 mb-0">
                        <thead><tr><th>Type</th><th>Total welds</th><th>Ungrouped</th><th>W/o (units)</th><th>Est. with (units)</th><th>Savings</th><th>%</th></tr></thead>
                        <tbody>
                          {data.map((r, i) => (
                            <tr key={i}>
                              <td><strong>{r.inspection_type_name}</strong></td>
                              <td>{r.total_welds_count}</td>
                              <td>{r.potential_welds_count}</td>
                              <td>{r.potential_without_units.toFixed(1)}</td>
                              <td>{r.potential_with_units.toFixed(1)}</td>
                              <td style={{ color: r.potential_savings_cost >= 0 ? 'green' : 'red' }}><strong>{r.potential_savings_cost.toFixed(2)}</strong></td>
                              <td style={{ color: r.potential_savings_pct >= 0 ? 'green' : 'red' }}>{r.potential_savings_pct}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Col>
                </Row>
              </div>
            )}
            {data && data.length === 0 && !loading && (
              <div className="text-muted">No inspection groups found for this project.</div>
            )}
          </CardBody>
        </Card>
      </div>
    )
  }
}

export default withApollo(GroupingSavings)



// WEBPACK FOOTER //
// ./src/components/Report/GroupingSavings.js